import { forwardRef, useEffect, useLayoutEffect, useRef } from 'react'
import styled from 'styled-components'
import { useWindowSize } from '../../../hooks'
import { Border, Colors, Spacing, Typography } from '../../../styles'
import { useCalendarContext } from '../../calendar/CalendarContext'
import { FontSize, PlainTextEditorProps } from './types'

const PlainTextArea = styled.textarea<{ fontSize: FontSize }>`
    background-color: inherit;
    outline: none;
    border: none;
    border-radius: ${Border.radius.medium};
    resize: none;
    width: 100%;
    box-sizing: border-box;
    height: 100%;
    padding: ${Spacing._8};
    white-space: pre-wrap;
    color: ${Colors.text.ink};
    ${({ fontSize }) => fontSize === 'small' && Typography.body.medium};
    ${({ fontSize }) => fontSize === 'medium' && Typography.title.appHeader};
    ${({ fontSize }) => fontSize === 'large' && Typography.headline.large};
    /* Only 'small' is under 16px; the other two already clear the zoom threshold. */
    ${({ fontSize }) => fontSize === 'small' && Typography.touchFieldFloor};
`

const PlainTextEditor = forwardRef((props: PlainTextEditorProps, textAreaRef) => {
    const { isFullHeight, maxHeight, value, onChange, autoFocus, ...rest } = props
    const ref = useRef<HTMLTextAreaElement | null>(null)
    const windowSize = useWindowSize()
    const { isCollapsed } = useCalendarContext()
    const resizeEditor = () => {
        if (!isFullHeight && ref.current) {
            ref.current.style.height = '0px'
            ref.current.style.height =
                maxHeight && ref.current.scrollHeight > maxHeight ? `${maxHeight}px` : `${ref.current.scrollHeight}px`
        }
    }

    useLayoutEffect(() => {
        if (ref.current) {
            ref.current.value = value
        }
    }, [value])

    useEffect(resizeEditor, [value, maxHeight, windowSize, isCollapsed])

    useEffect(() => {
        if (props.autoSelect && ref.current) {
            ref.current.select()
        }
    }, [])

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        if (ref.current && (e.key === 'Escape' || (props.enterBehavior === 'blur' && e.key === 'Enter'))) {
            ref.current.blur()
        }
        if (props.enterBehavior === 'disable' && e.key === 'Enter') {
            e.preventDefault()
        }
    }

    return (
        <PlainTextArea
            ref={(node) => {
                ref.current = node
                if (typeof textAreaRef === 'function') {
                    textAreaRef(node)
                } else if (textAreaRef !== null) {
                    textAreaRef.current = node
                }
            }}
            onChange={(e) => {
                resizeEditor()
                onChange(e.target.value)
            }}
            onKeyDown={handleKeyDown}
            autoFocus={autoFocus}
            data-autofocus={autoFocus}
            {...rest}
        />
    )
})

export default PlainTextEditor
